import { useSearchParams } from 'react-router-dom'

// Utility for generic URL param management using React Router v6+ useSearchParams
export const useAppSearchParams = (): {
  searchParams: URLSearchParams
  setUrlParams: (params: Record<string, string | null | undefined>) => void
  removeUrlParams: (keys: string | string[]) => void
  removeAllUrlParams: () => void
  setOnlyUrlParams: (params: Record<string, string | null | undefined>) => void
} => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Set or update one or more params
  const setUrlParams = (params: Record<string, string | null | undefined>): void => {
    const sp = new URLSearchParams(searchParams)
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        sp.delete(key)
      } else {
        sp.set(key, value)
      }
    })
    setSearchParams(sp, { replace: true })
  }

  // Remove one or more params
  const removeUrlParams = (keys: string | string[]): void => {
    setSearchParams((prev: URLSearchParams) => {
      const sp = new URLSearchParams(prev)
      ;(Array.isArray(keys) ? keys : [keys]).forEach(key => { sp.delete(key) })
      return sp
    })
  }

  // Remove all params
  const removeAllUrlParams = (): void => {
    setSearchParams(new URLSearchParams())
  }

  // Replace all params with new ones
  const setOnlyUrlParams = (params: Record<string, string | null | undefined>): void => {
    const sp = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        sp.set(key, value)
      }
    })
    setSearchParams(sp)
  }

  return { searchParams, setUrlParams, removeUrlParams, removeAllUrlParams, setOnlyUrlParams }
}

// Usage in your component:
// const { searchParams, setUrlParams, removeUrlParams, removeAllUrlParams, setOnlyUrlParams } = useAppSearchParams();
// setUrlParams({ projectId: '123', termId: '456' }); // Add or update params
// removeUrlParams(['projectId', 'termId']); // Remove specific params
// removeAllUrlParams(); // Remove all params from the URL
// setOnlyUrlParams({ foo: 'bar', baz: 'qux' }); // Remove all previous params and set only new ones
